import dayjs from 'dayjs';
import { useState } from 'react';
import { useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../../../components/ui/Alert';
import Card from '../../../components/ui/Card';
import { useOrder } from '../../../lib/hooks/use-orders';
import AcceptOrder from '../../../components/orders/AcceptOrder';
import RejectOrder from '../../../components/orders/RejectOrder';
import FulfillOrder from '../../../components/orders/FulfillOrder';
import Loader from '../../../components/ui/Loader';
import Back from '../../../components/ui/Back';
import Edit from '../../../components/ui/Edit';
import Button from '../../../components/ui/Button';
import { useCheckAccess } from '../../../lib/auth/use-checkAccess';
import Content from '../../../components/layout/Content';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComment,
  faUserCircle,
  faClock,
  faCheckCircle,
  faTimesCircle,
} from '@fortawesome/free-regular-svg-icons';
import { faTruck } from '@fortawesome/free-solid-svg-icons';
import { createTier2Fulfillment } from '../../../lib/db/tier2fulfillments';
import { Timestamp } from 'firebase/firestore';
import { useSale } from '../../../lib/hooks/use-sales';
import FulfillSale from '../../../components/sales/FulfillSale';
import RejectSale from '../../../components/sales/RejectSale';

type OrderDetailsParams = 'id';

const Sale = () => {
  const { id } = useParams<OrderDetailsParams>();
  const checkAccess = useCheckAccess();
  const navigate = useNavigate();
  const { sale, error, update, loading } = useSale(id);
  const [showAccept, setShowAccept] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showFulfill, setShowFulfill] = useState(false);
  const [showFulfillPartially, setShowFulfillPartially] = useState(false);
  const [showFulfillTier2, setShowFulfillTier2] = useState(false);
  const [displayEditButton, setDisplayEditButton] = useState(false);
  const [displayComment, setDisplayComment] = useState(false);
  const [displayAcceptButton, setDisplayAcceptButton] = useState(false);
  const [displayRejectButton, setDisplayRejectButton] = useState(false);
  const [displayFulfillButton, setDisplayFulfillButton] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    if (error) {
      setShowAlert(true);
    } else if (showAlert) {
      setShowAlert(false);
    }
    return () => {
      setShowAlert(false);
    };
  }, [error]);

  useEffect(() => {
    if (sale) {
      switch (sale.status) {
        case 'pending':
          setDisplayEditButton(true);
          setDisplayAcceptButton(true);
          setDisplayRejectButton(true);

          break;
        case 'accepted':
          setDisplayFulfillButton(true);
          setDisplayComment(true);

          break;
        case 'fulfilled':
          setDisplayComment(true);

          break;
        case 'rejected':
          setDisplayComment(true);

          break;
        default:
          break;
      }
    }
  }, [sale]);

  const handleAccept = async (data) => {
    await update({ status: 'accepted', comment: data.comment });
    navigate('/orders', { replace: true });
  };
  const handleReject = async (data) => {
    await update({ status: 'rejected', comment: data.comment });
    navigate('/orders', { replace: true });
  };
  const handleFulfill = async (data) => {
    const fulfillments = sale.fulfillments ? sale.fulfillments + 1 : 1;

    const classic = isNaN(data.classic) ? 0 : data.classic;
    const supaset = isNaN(data.supaset) ? 0 : data.supaset;
    const supafixc0 = isNaN(data.supafixc0) ? 0 : data.supafixc0;
    const supafixc1 = isNaN(data.supafixc1) ? 0 : data.supafixc1;
    const productsDelivered = {
      classic:
        sale.productsDelivered && sale.productsDelivered.classic
          ? sale.productsDelivered.classic + classic
          : classic,
      supaset:
        sale.productsDelivered && sale.productsDelivered.supaset
          ? sale.productsDelivered.supaset + supaset
          : supaset,
      supafixc0:
        sale.productsDelivered && sale.productsDelivered.supafixc0
          ? sale.productsDelivered.supafixc0 + supafixc0
          : supafixc0,
      supafixc1:
        sale.productsDelivered && sale.productsDelivered.supafixc1
          ? sale.productsDelivered.supafixc1 + supafixc1
          : supafixc1,
    };
    await update({
      status: 'fulfilled',
      productsDelivered,
      fulfillments: fulfillments,
    });
    navigate('/sales', { replace: true });
  };

  const handleFulfillPartially = async (data) => {
    const classic = isNaN(data.classic) ? 0 : data.classic;
    const supaset = isNaN(data.supaset) ? 0 : data.supaset;
    const supafixc0 = isNaN(data.supafixc0) ? 0 : data.supafixc0;
    const supafixc1 = isNaN(data.supafixc1) ? 0 : data.supafixc1;
    const productsDelivered = {
      classic:
        sale.productsDelivered && sale.productsDelivered.classic
          ? sale.productsDelivered.classic + classic
          : classic,
      supaset:
        sale.productsDelivered && sale.productsDelivered.supaset
          ? sale.productsDelivered.supaset + supaset
          : supaset,
      supafixc0:
        sale.productsDelivered && sale.productsDelivered.supafixc0
          ? sale.productsDelivered.supafixc0 + supafixc0
          : supafixc0,
      supafixc1:
        sale.productsDelivered && sale.productsDelivered.supafixc1
          ? sale.productsDelivered.supafixc1 + supafixc1
          : supafixc1,
    };

    const fulfillments = sale.fulfillments ? sale.fulfillments + 1 : 1;

    await update({
      productsDelivered,
      fulfillments: fulfillments,
    });
    navigate('/sales', { replace: true });
  };

  const handleFulfillPartiallyTier2 = async (data) => {
    const classic = isNaN(data.classic) ? 0 : data.classic;
    const supaset = isNaN(data.supaset) ? 0 : data.supaset;
    const supafixc0 = isNaN(data.supafixc0) ? 0 : data.supafixc0;
    const supafixc1 = isNaN(data.supafixc1) ? 0 : data.supafixc1;
    const productsDelivered = {
      classic:
        sale.productsDelivered && sale.productsDelivered.classic
          ? sale.productsDelivered.classic + classic
          : classic,
      supaset:
        sale.productsDelivered && sale.productsDelivered.supaset
          ? sale.productsDelivered.supaset + supaset
          : supaset,
      supafixc0:
        sale.productsDelivered && sale.productsDelivered.supafixc0
          ? sale.productsDelivered.supafixc0 + supafixc0
          : supafixc0,
      supafixc1:
        sale.productsDelivered && sale.productsDelivered.supafixc1
          ? sale.productsDelivered.supafixc1 + supafixc1
          : supafixc1,
    };

    await update({
      productsDelivered,
    });
  };

  return (
    <>
      {checkAccess(['sop', 'distributor']) && (
        <RejectSale
          open={showReject}
          onReject={handleReject}
          setOpen={(open) => setShowReject(open)}
        />
      )}
      {checkAccess(['fsa', 'sop', 'distributor']) && (
        <FulfillSale
          open={showFulfill}
          sale={sale}
          onFulfill={handleFulfill}
          setOpen={(open) => setShowFulfill(open)}
          confirmText="Are you sure you want to fulfill the sale?"
          buttonText="Fulfill"
        />
      )}
      {checkAccess(['fsa', 'sop', 'distributor']) && (
        <FulfillSale
          open={showFulfillPartially}
          sale={sale}
          onFulfill={handleFulfillPartially}
          setOpen={(open) => setShowFulfillPartially(open)}
          confirmText="Are you sure you want to partially fulfill the sale?"
          buttonText="Partially fulfill"
        />
      )}
      {checkAccess(['fsa', 'sop', 'distributor']) && (
        <FulfillSale
          open={showFulfillTier2}
          sale={sale}
          onFulfillTier2={handleFulfillPartiallyTier2}
          tier2={true}
          setOpen={(open) => setShowFulfillTier2(open)}
          confirmText="Are you sure you want to fulfill this tier 2 sale?"
          buttonText="Fulfil to tier 2 outlet"
        />
      )}
      <Loader show={loading} />
      <Alert
        message={error && error.message}
        open={showAlert}
        setOpen={(open) => setShowAlert(open)}
        title="Error"
      />
      <Content>
        {loading || sale ? (
          <Card className="grid grid-cols-10">
            <Back to="/sales" className="col-span-3" />
            <div className="col-span-4 flex justify-center items-center text-center text-xl font-bold text-lh-head-black">
              {sale && (
                <FontAwesomeIcon
                  icon={sale.createdBy === 'chatbot' ? faComment : faUserCircle}
                  className="mr-2 text-lh-head-black"
                />
              )}
              {sale && sale.id}
            </div>
            {displayEditButton && (
              <Edit className="col-span-3 flex-row-reverse" />
            )}
            <h2 className="col-span-8 font-bold text-lg text-lh-head-black">
              {sale && sale.name}
              <p className="col-span-full text-sm font-thin text-lh-text-black">
                {sale && dayjs(sale.createdAt).format('DD.MM.YYYY HH:mm')}
              </p>
            </h2>
            <div className="col-span-2 flex flex-col items-center font-bold text-lg text-lh-head-black">
              <p>Status</p>
              {sale &&
                sale.status &&
                (sale.status === 'pending' ? (
                  <FontAwesomeIcon icon={faClock} className="text-2xl mt-0" />
                ) : sale.status === 'accepted' ? (
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="text-2xl mt-0"
                  />
                ) : sale.status === 'rejected' ? (
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    className="text-2xl mt-0"
                  />
                ) : sale.status === 'fulfilled' ? (
                  <FontAwesomeIcon icon={faTruck} className="text-2xl mt-0" />
                ) : (
                  ''
                ))}
            </div>
            <hr className="col-span-full mt-2 mb-2" />
            <h2 className="col-span-full font-bold text-lh-head-black">
              Distributor
            </h2>
            <p className="col-span-full text-lh-text-black">
              {sale && sale.distributor.name}
            </p>
            <h2 className="col-span-full font-bold mt-4 mb-2 text-lh-head-black">
              Products fulfilled / ordered
            </h2>
            <h3 className="col-span-2 py-1 text-lh-text-black">Classic</h3>
            <p className="col-span-3 col-start-7 mb-4 text-right py-1 px-3 font-semibold text-lh-text-black">
              {sale &&
                (sale.productsDelivered
                  ? sale.productsDelivered.classic
                  : 0)}{' '}
              {' / '}
              {sale && sale.products.classic}
            </p>
            <p className="col-span-1 py-1 text-gray-500 font-thin">bags</p>
            <h3 className="col-span-2 py-1 text-lh-text-black">Supaset</h3>
            <p className="col-span-3 col-start-7 mb-4 text-right py-1 px-3 font-semibold text-lh-text-black">
              {sale &&
                (sale.productsDelivered
                  ? sale.productsDelivered.supaset
                  : 0)}{' '}
              {' / '}
              {sale && sale.products.supaset}
            </p>
            <p className="col-span-1 py-1 text-gray-500 font-thin">bags</p>
            <h3 className="col-span-2 py-1 text-lh-text-black">SupafixC0</h3>
            <p className="col-span-3 col-start-7 mb-4 text-right py-1 px-3 font-semibold text-lh-text-black">
              {sale &&
                (sale.productsDelivered
                  ? sale.productsDelivered.supafixc0
                  : 0)}{' '}
              {' / '}
              {sale && sale.products.supafixc0}
            </p>
            <p className="col-span-1 py-1 text-gray-500 font-thin">bags</p>
            <h3 className="col-span-2 py-1 text-lh-text-black">SupafixC1</h3>
            <p className="col-span-3 col-start-7 mb-4 text-right py-1 px-3 font-semibold text-lh-text-black">
              {sale &&
                (sale.productsDelivered
                  ? sale.productsDelivered.supafixc1
                  : 0)}{' '}
              {' / '}
              {sale && sale.products.supafixc1}
            </p>
            <p className="col-span-1 py-1 text-gray-500 font-thin">bags</p>
            <h2 className="col-span-full font-bold mb-2 text-lh-head-black">
              Delivery Address
            </h2>
            <p className="col-span-full rounded-lg mb-4 py-1 px-3 border-2 text-lh-text-black">
              {sale && sale.address}
            </p>

            {checkAccess(['sop', 'distributor']) && displayAcceptButton && (
              <Button
                onClick={() => setShowAccept(true)}
                color="green"
                type="button"
                className="col-span-full mt-2 py-2"
              >
                Accept
              </Button>
            )}
            {checkAccess(['sop', 'distributor']) && displayRejectButton && (
              <Button
                onClick={() => setShowReject(true)}
                color="red"
                type="button"
                className="col-span-full mt-2 py-2"
              >
                Reject
              </Button>
            )}
            {checkAccess(['fsa', 'sop', 'distributor']) &&
              displayFulfillButton && (
                <Button
                  onClick={() => setShowFulfill(true)}
                  color="blue"
                  type="button"
                  className="col-span-full mt-2 py-2"
                >
                  Fulfil
                </Button>
              )}
          </Card>
        ) : (
          <Card>Order not found!</Card>
        )}
      </Content>
    </>
  );
};

export default Sale;
